const BERLIN_TIME_ZONE = 'Europe/Berlin';

export const buildDepartmentAccounts = (subaccountBalances = {}, departments = {}) => {
  const hasDepartmentDefinitions = Array.isArray(departments);
  const departmentList = hasDepartmentDefinitions
    ? departments
    : Object.entries(departments).map(([id, name]) => ({ id, name }));
  const departmentsById = Object.fromEntries(departmentList.map(department => [department.id, department]));
  const accountIds = hasDepartmentDefinitions
    ? [
        ...departmentList.map(department => department.id),
        ...Object.keys(subaccountBalances).filter(id => !departmentsById[id]),
      ]
    : Object.keys(subaccountBalances);

  return accountIds.map(id => {
    const values = subaccountBalances[id] || {};
    const department = departmentsById[id] || {};
    return {
      id,
      name: values.department_name || department.name || 'Unbekannte Wachabteilung',
      useCombined: values.use_combined === undefined
        ? !!department.use_combined_accounts
        : !!values.use_combined,
      breakfast: Number(values.breakfast) || 0,
      drinks: Number(values.drinks) || 0,
      total: Number(values.total ?? values.combined) || 0,
    };
  });
};

const getBerlinDateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'unknown';

  const parts = new Intl.DateTimeFormat('de-DE', {
    timeZone: BERLIN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const byType = Object.fromEntries(parts.map(part => [part.type, part.value]));

  return `${byType.year}-${byType.month}-${byType.day}`;
};

export const getHistoryCategories = (entry) => {
  if (entry.type === 'payment') return ['payments'];
  if (entry.order_type === 'coffee_flat') return Number(entry.total_price) < 0 ? ['breakfast', 'cancelled'] : ['breakfast'];

  const categories = [];
  if (entry.order_type === 'drinks') categories.push('drinks');
  if (entry.order_type === 'sweets') categories.push('snacks');
  if (entry.order_type === 'category') {
    const categoryId = entry.visual_category_id || entry.category_id || entry.menu_category_id;
    const categoryName = entry.visual_category_name || entry.category_name || entry.menu_category_name;
    categories.push(`catalog:${categoryId || categoryName || 'other'}`);
  }

  if (entry.order_type === 'breakfast') {
    const readableItems = entry.readable_items || [];
    const hasLunch = readableItems.some(item => item.category === 'lunch');
    const hasDinner = readableItems.some(item => item.category === 'dinner');
    const hasBreakfast = readableItems.length === 0 || readableItems.some(
      item => item.category !== 'lunch' && item.category !== 'dinner'
    );

    if (hasBreakfast) categories.push('breakfast');
    if (hasLunch || hasDinner) categories.push('lunch');
  }

  if (entry.is_cancelled || entry.order_type === 'deletion') {
    categories.push('cancelled');
  }

  return categories;
};

export const filterHistoryEntries = (entries, filter) => {
  if (!filter || filter === 'all') return entries;
  return entries.filter(entry => getHistoryCategories(entry).includes(filter));
};

export const getHistoryGroupDescriptor = (entry) => {
  if (entry.order_type === 'coffee_flat') {
    return { key: 'coffee-flat', label: 'Kaffee-Monatsflat', groupable: true };
  }
  if (entry.type === 'payment' || entry.order_type === 'deletion') {
    return {
      key: `entry:${entry.id || entry.timestamp}`,
      label: '',
      groupable: false,
    };
  }

  if (entry.order_type === 'breakfast') {
    return { key: 'meals', label: 'Frühstück & Mahlzeiten', groupable: true };
  }

  const dynamicCategoryId = entry.visual_category_id
    || entry.category_id
    || entry.product_category_id;
  const dynamicCategoryName = entry.visual_category_name
    || entry.category_name
    || entry.product_category_name;

  if (dynamicCategoryId || dynamicCategoryName) {
    return {
      key: `catalog:${dynamicCategoryId || dynamicCategoryName}`,
      label: dynamicCategoryName || 'Weitere Artikel',
      groupable: true,
    };
  }

  if (entry.order_type === 'drinks') {
    return { key: 'drinks', label: 'Getränke', groupable: true };
  }
  if (entry.order_type === 'sweets') {
    return { key: 'snacks', label: 'Snacks', groupable: true };
  }

  return {
    key: `catalog:${entry.order_type || 'other'}`,
    label: entry.order_type_label || entry.order_type || 'Weitere Artikel',
    groupable: true,
  };
};

export const groupHistoryEntriesByCategory = (entries) => {
  const grouped = [];
  const byKey = new Map();

  entries.forEach(entry => {
    const descriptor = getHistoryGroupDescriptor(entry);
    if (!descriptor.groupable) {
      grouped.push({ kind: 'entry', id: descriptor.key, entry, timestamp: entry.timestamp });
      return;
    }

    if (!byKey.has(descriptor.key)) {
      const group = {
        kind: 'category',
        id: `category:${descriptor.key}`,
        key: descriptor.key,
        label: descriptor.label,
        entries: [],
        timestamp: entry.timestamp,
      };
      byKey.set(descriptor.key, group);
      grouped.push(group);
    }
    byKey.get(descriptor.key).entries.push(entry);
  });

  return grouped;
};

const PAYPAL_ME_HOSTS = new Set(['paypal.me', 'www.paypal.me']);
const PAYPAL_AMOUNT_SEGMENT = /^\d+(?:[.,]\d{1,2})?(?:[a-z]{3})?$/i;

export const appendOpenAmountToPayPalMeLink = (link, balance) => {
  const numericBalance = Number(balance);
  if (!link || !Number.isFinite(numericBalance) || numericBalance >= 0) return link || '';

  const candidate = /^https?:\/\//i.test(link) ? link : `https://${link}`;
  try {
    const url = new URL(candidate);
    if (!PAYPAL_ME_HOSTS.has(url.hostname.toLowerCase())) return link;

    const pathSegments = url.pathname.split('/').filter(Boolean);
    if (pathSegments.length === 0) return link;
    if (pathSegments.length > 1 && PAYPAL_AMOUNT_SEGMENT.test(pathSegments[pathSegments.length - 1])) {
      pathSegments.pop();
    }
    pathSegments.push(`${Math.abs(numericBalance).toFixed(2)}EUR`);
    url.pathname = `/${pathSegments.join('/')}`;
    return url.toString();
  } catch (error) {
    return link;
  }
};

export const getPayPalLink = (settings, accountType, balance = null) => {
  if (!settings?.enabled) return '';

  if (accountType === 'breakfast' && !settings.breakfast_enabled) return '';
  if (accountType === 'drinks' && !settings.drinks_enabled) return '';
  if (accountType === 'combined' && !settings.breakfast_enabled && !settings.drinks_enabled) return '';

  let paymentLink = '';

  if (!settings.use_separate_links) {
    paymentLink = settings.combined_link || '';
  } else if (accountType === 'breakfast') {
    paymentLink = settings.breakfast_link || '';
  } else if (accountType === 'drinks') {
    paymentLink = settings.drinks_link || '';
  } else if (accountType === 'combined') {
    if (settings.breakfast_enabled && settings.breakfast_link) paymentLink = settings.breakfast_link;
    else if (settings.drinks_enabled && settings.drinks_link) paymentLink = settings.drinks_link;
  }

  return settings.append_open_amount
    ? appendOpenAmountToPayPalMeLink(paymentLink, balance)
    : paymentLink;
};

export const calculateEmployeeOrderTotal = (entry) => {
  if (entry.employee_total_price !== undefined && entry.employee_total_price !== null) {
    return Number(entry.employee_total_price) || 0;
  }
  return Number(entry.total_price) || 0;
};

export const groupHistoryEntriesByDay = (entries, now = new Date()) => {
  const todayKey = getBerlinDateKey(now);
  const [year, month, day] = todayKey.split('-').map(Number);
  const yesterday = new Date(Date.UTC(year, month - 1, day - 1));
  const yesterdayKey = [
    yesterday.getUTCFullYear(),
    String(yesterday.getUTCMonth() + 1).padStart(2, '0'),
    String(yesterday.getUTCDate()).padStart(2, '0'),
  ].join('-');
  const groups = [];
  const byKey = new Map();

  entries.forEach(entry => {
    const key = getBerlinDateKey(entry.timestamp);
    if (!byKey.has(key)) {
      const date = new Date(entry.timestamp);
      let label;

      if (key === todayKey) label = 'Heute';
      else if (key === yesterdayKey) label = 'Gestern';
      else if (key === 'unknown') label = 'Ohne Datum';
      else {
        label = new Intl.DateTimeFormat('de-DE', {
          timeZone: BERLIN_TIME_ZONE,
          weekday: 'long',
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        }).format(date);
      }

      const group = { key, label, items: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    byKey.get(key).items.push(entry);
  });

  return groups;
};
